<template>
  <div
    v-if="open"
    class="signup-backdrop restore open"
    @mousedown="onBackdropMouseDown"
  >
    <div
      ref="modal"
      class="signup-modal restore"
      role="dialog"
      aria-modal="true"
      aria-labelledby="restore-title"
    >
      <div class="sheet-handle" aria-hidden="true" />
      <button
        type="button"
        class="signup-close"
        aria-label="Schließen"
        :disabled="submitting"
        @click="close"
      >
        <svg
          viewBox="0 0 24 24"
          width="18"
          height="18"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
        >
          <path d="M6 6 L18 18 M18 6 L6 18" />
        </svg>
      </button>

      <div class="signup-modal-body">
        <template v-if="stage === 'form'">
          <div class="login-mark">
            <VibelaMark :size="42" />
          </div>
          <div class="step-eyebrow login-eyebrow">Konto wiederherstellen</div>
          <h2 id="restore-title" class="step-h2 login-headline">
            Setz ein neues <em class="italic">Passwort</em>.
          </h2>
          <p class="step-sub restore-lead">
            {{ description }}
          </p>

          <form novalidate @submit.prevent="submit">
            <div class="field restore-field">
              <label class="field-label" for="restore-password">{{ passwordLabel }}</label>
              <div class="field-input-wrap">
                <input
                  id="restore-password"
                  v-model="password"
                  :type="showPw ? 'text' : 'password'"
                  class="field-input has-icon"
                  :class="{ error: !!errors.password }"
                  autocomplete="new-password"
                  :placeholder="passwordPlaceholder"
                />
                <button
                  type="button"
                  class="field-toggle"
                  :aria-label="passwordLabel"
                  @click="showPw = !showPw"
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="18"
                    height="18"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                </button>
              </div>
              <div v-if="errors.password" class="field-error">{{ errors.password }}</div>
            </div>

            <div class="field restore-field">
              <label class="field-label" for="restore-password-confirm">{{ confirmLabel }}</label>
              <div class="field-input-wrap">
                <input
                  id="restore-password-confirm"
                  v-model="passwordConfirmation"
                  :type="showConfirm ? 'text' : 'password'"
                  class="field-input has-icon"
                  :class="{ error: !!errors.password_confirmation }"
                  autocomplete="new-password"
                  :placeholder="confirmPlaceholder"
                />
                <button
                  type="button"
                  class="field-toggle"
                  :aria-label="confirmLabel"
                  @click="showConfirm = !showConfirm"
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="18"
                    height="18"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                </button>
              </div>
              <div v-if="errors.password_confirmation" class="field-error">
                {{ errors.password_confirmation }}
              </div>
            </div>

            <p v-if="submitError" class="field-error restore-submit-error">{{ submitError }}</p>

            <button
              type="submit"
              class="btn btn-primary restore-submit"
              :disabled="submitting"
            >
              {{ submitting ? 'Wird gespeichert…' : submitLabel }}
              <span aria-hidden="true">→</span>
            </button>
          </form>
        </template>

        <template v-else>
          <div class="login-mark">
            <VibelaMark :size="42" />
          </div>
          <div class="step-eyebrow login-eyebrow">Geschafft</div>
          <h2 class="step-h2 login-headline">
            Willkommen <em class="italic">zurück</em>.
          </h2>
          <p class="step-sub restore-lead">
            Dein Konto wurde wiederhergestellt. Du wirst gleich weitergeleitet…
          </p>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import VibelaMark from './VibelaMark.vue';

const PATH_RE = /\/restore-account\/([^/?#]+)/;

export default {
  name: 'RestoreAccountModal',
  components: { VibelaMark },
  data() {
    return {
      open: false,
      stage: 'form',
      hash: '',
      password: '',
      passwordConfirmation: '',
      showPw: false,
      showConfirm: false,
      submitting: false,
      submitError: '',
      errors: {},
      bodyOverflow: '',
    };
  },
  computed: {
    description() {
      return this.t('description', 'Ändere dein Passwort, um dein Konto wiederherzustellen.');
    },
    passwordLabel() {
      return this.t('password', 'Neues Passwort');
    },
    confirmLabel() {
      return this.t('password_confirmation', 'Passwort wiederholen');
    },
    submitLabel() {
      return this.t('submit', 'Konto wiederherstellen');
    },
    passwordPlaceholder() {
      return 'Mindestens 8 Zeichen';
    },
    confirmPlaceholder() {
      return 'Zur Sicherheit nochmal';
    },
  },
  created() {
    this.tryOpenFromPath();
  },
  beforeDestroy() {
    this.unlockScroll();
    document.removeEventListener('keydown', this.onKeyDown);
  },
  watch: {
    open(isOpen) {
      if (isOpen) {
        this.lockScroll();
        this.$nextTick(() => {
          document.addEventListener('keydown', this.onKeyDown);
          const input = this.$el && this.$el.querySelector('#restore-password');
          if (input) {
            input.focus();
          }
        });
      } else {
        this.unlockScroll();
        document.removeEventListener('keydown', this.onKeyDown);
      }
    },
  },
  methods: {
    t(key, fallback) {
      try {
        const value = this.trans && this.trans.get(`pages:restore_account.${key}`);
        if (value && value !== `pages:restore_account.${key}`) {
          return value;
        }
      } catch (e) {
        // fall through
      }
      return fallback;
    },
    tryOpenFromPath() {
      const match = (window.location.pathname || '').match(PATH_RE);
      if (!match) {
        return;
      }
      this.hash = decodeURIComponent(match[1]);
      this.open = true;
    },
    close() {
      if (this.submitting) {
        return;
      }
      this.open = false;
      if (window.history && typeof window.history.replaceState === 'function') {
        window.history.replaceState({}, '', '/');
      }
    },
    onBackdropMouseDown(event) {
      if (event.target === event.currentTarget) {
        this.close();
      }
    },
    onKeyDown(event) {
      if (event.key === 'Escape') {
        this.close();
      }
    },
    lockScroll() {
      this.bodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    },
    unlockScroll() {
      document.body.style.overflow = this.bodyOverflow;
    },
    async submit() {
      if (this.submitting) {
        return;
      }
      this.errors = {};
      this.submitError = '';

      if (!this.password || this.password.length < 8) {
        this.errors = { password: 'Bitte gib ein Passwort mit mindestens 8 Zeichen ein.' };
        return;
      }
      if (this.password !== this.passwordConfirmation) {
        this.errors = { password_confirmation: 'Die Passwörter stimmen nicht überein.' };
        return;
      }

      this.submitting = true;
      try {
        const response = await axios.post(zRoute('ajax.restore_account'), {
          hash: this.hash,
          password: this.password,
          password_confirmation: this.passwordConfirmation,
        });
        if (response.data && response.data.status === 'success') {
          this.stage = 'done';
          window.location.href = zRoute('home');
          return;
        }
        this.submitError = 'Wir konnten dein Konto nicht wiederherstellen. Der Link ist eventuell abgelaufen.';
      } catch (err) {
        const data = err && err.response && err.response.data;
        if (data && data.errors) {
          const flat = {};
          Object.keys(data.errors).forEach((key) => {
            const value = data.errors[key];
            flat[key] = Array.isArray(value) ? value[0] : value;
          });
          this.errors = flat;
          if (flat.hash) {
            this.submitError = flat.hash;
          }
        } else {
          this.submitError = 'Wir konnten dein Konto nicht wiederherstellen. Der Link ist eventuell abgelaufen.';
        }
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.login-mark {
  text-align: center;
  margin: 12px 0 18px;
}

.login-eyebrow {
  display: flex;
  justify-content: center;
}

.login-headline {
  text-align: center;
}

.restore-lead {
  text-align: center;
  margin-left: auto;
  margin-right: auto;
}

.restore-field {
  margin-bottom: 16px;
}

.restore-submit {
  width: 100%;
  margin-top: 8px;
}

.restore-submit-error {
  margin-bottom: 12px;
}

.field-input.error {
  border-color: #c84161;
  background: rgba(200, 65, 97, 0.04);
}
</style>
